import React, { useState } from 'react';
import Accordion from '../../../../Accordion';
import {
  Activity,
  BarChart3,
  Calculator,
  Database,
  Globe,
  Info,
  TrendingUp,
  Zap
} from 'lucide-react';

const OverviewDocumentationAccordion = () => {
  const [activeKey, setActiveKey] = useState(null);

  return (
    <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 md:px-6 lg:px-8 py-4 md:py-5 border-b border-gray-200">
        <div className="flex items-center gap-3 md:gap-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Info className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-base md:text-lg lg:text-xl font-semibold text-gray-700">Data Sources & Methodology</h3>
            <p className="text-sm md:text-base text-gray-500 mt-0.5">
              High-level snapshot of country energy system performance and trends
            </p>
          </div>
        </div>
      </div>

      <Accordion activeKey={activeKey} onSelect={(k) => setActiveKey(k)} className="border-0">
        <Accordion.Item eventKey="0" className="border-0 border-b border-gray-100">
          <Accordion.Header className="px-4 md:px-6 lg:px-8 py-4 md:py-5 hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-3 w-full">
              <div className="p-2 bg-orange-100 rounded-lg">
                <TrendingUp className="h-4 w-4 text-orange-600" />
              </div>
              <div className="text-left">
                <h4 className="text-sm md:text-base lg:text-lg font-semibold text-gray-900 mb-0">Fossil Fuel Utilization Factor</h4>
                <p className="text-xs md:text-sm text-gray-500 mt-0.5 mb-0">Annual fossil fuel power plant utilization</p>
              </div>
            </div>
          </Accordion.Header>
          <Accordion.Body className="px-4 md:px-6 lg:px-8 pb-4 md:pb-5">
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Database className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Data Source</p>
                  <p className="text-xs lg:text-sm text-gray-700">EMBER - Global Electricity Review</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Calculator className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Processing</p>
                  <p className="text-xs lg:text-sm text-gray-700">
                    Calculated from capacity and generation data. Utilization Factor = Generation / (Capacity x 8760
                    hours).
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Note</p>
                  <p className="text-xs lg:text-sm text-gray-700">
                    Chart includes comparison to R10 regional average. R10 regions are IPCC AR6 climate modeling
                    groupings. The world is divided into 10 regions to enable consistent comparison across global
                    climate scenarios and energy pathways.
                  </p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 mt-3">
                <div className="flex items-center gap-2 mb-2">
                  <Globe className="h-4 w-4 text-gray-500" />
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">R10 Regions</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  <div className="text-xs text-gray-600 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                    <span><strong>R10AFRICA</strong> - Sub-Saharan Africa</span>
                  </div>
                  <div className="text-xs text-gray-600 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                    <span><strong>R10CHINA+</strong> - China and surrounding areas</span>
                  </div>
                  <div className="text-xs text-gray-600 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                    <span><strong>R10EUROPE</strong> - Europe (EU and neighbors)</span>
                  </div>
                  <div className="text-xs text-gray-600 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                    <span><strong>R10INDIA+</strong> - India and South Asia</span>
                  </div>
                  <div className="text-xs text-gray-600 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                    <span><strong>R10LATIN_AM</strong> - Latin America</span>
                  </div>
                  <div className="text-xs text-gray-600 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                    <span><strong>R10MIDDLE_EAST</strong> - Middle East and North Africa</span>
                  </div>
                  <div className="text-xs text-gray-600 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                    <span><strong>R10NORTH_AM</strong> - North America</span>
                  </div>
                  <div className="text-xs text-gray-600 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                    <span><strong>R10PAC_OECD</strong> - Pacific OECD (Japan, Australia, South Korea)</span>
                  </div>
                  <div className="text-xs text-gray-600 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                    <span><strong>R10REF_ECON</strong> - Reforming economies (Russia, Central Asia)</span>
                  </div>
                  <div className="text-xs text-gray-600 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                    <span><strong>R10REST_ASIA</strong> - Rest of Asia and Pacific</span>
                  </div>
                </div>
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="1" className="border-0 border-b border-gray-100">
          <Accordion.Header className="px-4 md:px-6 lg:px-8 py-4 md:py-5 hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-3 w-full">
              <div className="p-2 bg-red-100 rounded-lg">
                <Activity className="h-4 w-4 text-red-600" />
              </div>
              <div className="text-left">
                <h4 className="text-sm md:text-base lg:text-lg font-semibold text-gray-900 mb-0">CO2 Intensity</h4>
                <p className="text-xs md:text-sm text-gray-500 mt-0.5 mb-0">Annual CO2 emissions per unit of electricity</p>
              </div>
            </div>
          </Accordion.Header>
          <Accordion.Body className="px-4 md:px-6 lg:px-8 pb-4 md:pb-5">
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Database className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Data Source</p>
                  <p className="text-xs lg:text-sm text-gray-700">EMBER - CO2 Emissions Data</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Calculator className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Processing</p>
                  <p className="text-xs lg:text-sm text-gray-700">None. Direct annual emissions intensity data (kgCO2/MWh).</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Note</p>
                  <p className="text-xs lg:text-sm text-gray-700">
                    Chart includes comparison to R10 regional average (see above for region definitions).
                  </p>
                </div>
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="2" className="border-0 border-b border-gray-100">
          <Accordion.Header className="px-4 md:px-6 lg:px-8 py-4 md:py-5 hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-3 w-full">
              <div className="p-2 bg-green-100 rounded-lg">
                <BarChart3 className="h-4 w-4 text-green-600" />
              </div>
              <div className="text-left">
                <h4 className="text-sm md:text-base lg:text-lg font-semibold text-gray-900 mb-0">Generation Trends</h4>
                <p className="text-xs md:text-sm text-gray-500 mt-0.5 mb-0">Annual electricity generation by fuel type</p>
              </div>
            </div>
          </Accordion.Header>
          <Accordion.Body className="px-4 md:px-6 lg:px-8 pb-4 md:pb-5">
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Database className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Data Sources</p>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      <p className="text-xs lg:text-sm text-gray-700"><strong>Renewables</strong> (Hydro, Solar, Wind, Bioenergy): IRENA - Renewable Energy Statistics</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-gray-500 rounded-full"></span>
                      <p className="text-xs lg:text-sm text-gray-700"><strong>Non-Renewables</strong> (Coal, Gas, Oil, Nuclear): EMBER - Global Electricity Review</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Calculator className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Processing</p>
                  <p className="text-xs lg:text-sm text-gray-700">
                    Annual generation data aggregated by fuel type. Stacked to show total generation evolution
                    (2000-2025).
                  </p>
                </div>
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="3" className="border-0">
          <Accordion.Header className="px-4 md:px-6 lg:px-8 py-4 md:py-5 hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-3 w-full">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Zap className="h-4 w-4 text-purple-600" />
              </div>
              <div className="text-left">
                <h4 className="text-sm md:text-base lg:text-lg font-semibold text-gray-900 mb-0">Capacity Evolution</h4>
                <p className="text-xs md:text-sm text-gray-500 mt-0.5 mb-0">Installed electricity generation capacity by fuel</p>
              </div>
            </div>
          </Accordion.Header>
          <Accordion.Body className="px-4 md:px-6 lg:px-8 pb-4 md:pb-5">
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Database className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Data Sources</p>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      <p className="text-xs lg:text-sm text-gray-700"><strong>Renewables</strong> (Hydro, Solar, Wind, Bioenergy): IRENA - Renewable Energy Statistics</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-gray-500 rounded-full"></span>
                      <p className="text-xs lg:text-sm text-gray-700"><strong>Non-Renewables</strong> (Coal, Gas, Oil, Nuclear): EMBER - Capacity Data</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Calculator className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Processing</p>
                  <p className="text-xs lg:text-sm text-gray-700">
                    Annual capacity data aggregated by fuel type. Stacked to show total installed capacity evolution
                    (2000-2025).
                  </p>
                </div>
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  );
};

export default OverviewDocumentationAccordion;
